<template>
  <div class="page-root">
    <h1 class="font-weight-bold mb-4" style="font-family: maplestory">🐾 {{ $t("nav.training") }}</h1>

    <v-tabs v-model="tab" color="#8e6fd6" slider-color="#8e6fd6">
      <v-tab v-for="l in layers" :key="l.key">{{ l.emoji }} {{ l.title }}</v-tab>
      <v-tab>💗 {{ $t("nav.viewerAffinity") }}</v-tab>
    </v-tabs>

    <!-- 진짜 탭 콘텐츠: 탭마다 독립적으로 채운다. 공유 펫(단일 노드)은 활성 탭 슬롯으로 이동. -->
    <v-tabs-items v-model="tab">
      <!-- eager: 모든 탭 슬롯을 미리 mount → 탭 전환 즉시 슬롯이 DOM에 있어 펫 attach가 건너뛰지 않음 -->
      <v-tab-item v-for="(l, i) in layers" :key="l.key" eager>
        <div class="layer">
          <div class="layer__head">
            <span class="layer__emoji">{{ l.emoji }}</span>
            <div>
              <div class="layer__title">
                {{ l.title }} · <span class="layer__meta">{{ l.meta }}</span>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on, attrs }">
                    <v-icon small class="ml-1 layer__info" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                  </template>
                  <div>{{ l.desc }}</div>
                  <div class="layer__tip-quote">{{ l.lifespan }}</div>
                </v-tooltip>
              </div>
              <div class="layer__lifespan">“{{ l.quote }}”</div>
            </div>
          </div>

          <!-- 이 탭의 콘텐츠: WordCloud(위) + 공유 펫(바로 아래) 를 한 그룹으로 가운데 정렬 -->
          <div class="stage">
            <div class="stage__group">
              <brain-word-cloud class="stage__cloud" :words="wordsForLayer(l)"></brain-word-cloud>
              <div class="stage__pet" :data-idx="i"></div>
            </div>
          </div>

          <!-- 훈련(TRAIN) 탭: 모델 아래에 방송 룰 편집기(키워드/도네이션) — 구 방송하기 룰 섹션 -->
          <div v-if="l.key === 'TRAIN'" class="train-rules">
            <broadcast-rule-editor
              @play="onPlayReaction"
              @keyword-rules-changed="onKeywordRulesChanged"
            ></broadcast-rule-editor>
          </div>
        </div>
      </v-tab-item>

      <!-- 시청자 친밀도 (구 /pet/affinity 페이지를 탭으로 이관 — 준비 중) -->
      <v-tab-item eager>
        <div class="affinity">
          <div class="affinity__emoji">💗</div>
          <h2 class="affinity__title">{{ $t("nav.viewerAffinity") }}</h2>
          <p class="affinity__desc">{{ $t("categoryHub.desc.viewerAffinity") }} (준비 중)</p>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import BrainWordCloud from "@/views/Lab/BrainWordCloud.vue";
import BroadcastRuleEditor from "@/views/LiveRemote/fragments/BroadcastRuleEditor.vue";
import * as modelHost from "@/domain/modelHost";

// 키워드 룰 생명주기 4계층 (docs/design/키워드-룰-생명주기-계층.md)
//  탄생(BIRTH) · 성장(GROWTH) · 훈련(TRAIN) · 문맥(SESSION)
const LAYERS = [
  {
    key: "BIRTH",
    emoji: "🐣",
    title: "탄생",
    meta: "태어남",
    lifespan: "영구 · 개체의 초기값",
    desc: "개체가 세상에 나올 때 이미 가지고 있는 반응. 온보딩 시 주입되는 기본 소양(뱅온·방종 인사 등)이라, 아무것도 안 해도 최소한의 반응을 합니다.",
    quote: "생명체는 백지가 아니라 본능을 가지고 태어난다.",
    mockWords: [
      { word: "안녕하세요", weight: 10 },
      { word: "뱅온", weight: 9 },
      { word: "방종", weight: 8 },
      { word: "반가워", weight: 7 },
      { word: "어서와", weight: 6 },
      { word: "굿밤", weight: 6 },
      { word: "ㅎㅇ", weight: 5 },
      { word: "첫방문", weight: 4 },
      { word: "하이", weight: 4 },
      { word: "인사", weight: 3 },
    ],
  },
  {
    key: "GROWTH",
    emoji: "🌱",
    title: "성장",
    meta: "여러 세션간 학습",
    lifespan: "개체 생애 · 쌓이고 다듬어짐",
    desc: "방송을 거듭하며 펫이 스스로 배워 쌓는 반응. 자주 등장하고 반응이 좋았던 문맥이 항구적 기억으로 승격됩니다.",
    quote: "성장은 경험이 기억으로 굳는 과정이다.",
    mockWords: [
      { word: "ㅋㅋㅋ", weight: 12 },
      { word: "레전드", weight: 9 },
      { word: "킹받네", weight: 8 },
      { word: "폼미쳤다", weight: 7 },
      { word: "갓생", weight: 6 },
      { word: "오키", weight: 6 },
      { word: "억까", weight: 5 },
      { word: "인정", weight: 5 },
      { word: "단골", weight: 4 },
      { word: "고정멤버", weight: 3 },
    ],
  },
  {
    key: "TRAIN",
    emoji: "🎓",
    title: "훈련",
    meta: "주인이 직접 가르치기",
    lifespan: "영구 · 주인이 고칠 때까지",
    desc: "주인이 “이 말엔 이렇게 반응해”라고 직접 가르친 반응. 가장 강한 의지의 표현이며, 주인만이 만들고 고칩니다.",
    quote: "훈련은 개체와 주인 사이의 약속이다.",
    mockWords: [
      { word: "룰렛", weight: 10 },
      { word: "미션", weight: 9 },
      { word: "이벤트", weight: 8 },
      { word: "참여", weight: 7 },
      { word: "구독", weight: 7 },
      { word: "팔로우", weight: 6 },
      { word: "공지", weight: 5 },
      { word: "규칙", weight: 5 },
      { word: "보상", weight: 4 },
      { word: "명령어", weight: 3 },
    ],
  },
  {
    key: "SESSION",
    emoji: "💬",
    title: "문맥",
    meta: "채팅 세션 · 휘발",
    lifespan: "세션 · 끝나면 사라짐",
    desc: "지금 이 방송의 흐름에만 반응하는 임시 룰. 오늘 갑자기 뜬 밈·화제. 세션이 끝나면 함께 사라집니다.",
    quote: "문맥은 기억이 아니라 지금의 공기다.",
    mockWords: [
      { word: "실화냐", weight: 11 },
      { word: "ㄷㄷ", weight: 9 },
      { word: "소름", weight: 8 },
      { word: "대박", weight: 7 },
      { word: "방금그거", weight: 6 },
      { word: "지금", weight: 6 },
      { word: "오늘밈", weight: 5 },
      { word: "이순간", weight: 5 },
      { word: "헐", weight: 4 },
      { word: "미쳤다", weight: 3 },
    ],
  },
];

export default {
  name: "TrainingPage",
  components: { BrainWordCloud, BroadcastRuleEditor },
  data() {
    // 기본 탭 = 훈련(TRAIN). ?tab=N 이 명시되면 그걸 우선(0 포함).
    const trainIdx = LAYERS.findIndex((l) => l.key === "TRAIN");
    const q = this.$route.query.tab;
    const hasQuery = q !== undefined && q !== "";
    return {
      tab: hasQuery ? Number(q) : trainIdx >= 0 ? trainIdx : 0,
      layers: LAYERS,
      trainWords: [], // 훈련 탭 WordCloud: reaction_keyword_rule 실제 키워드(멘션 제외)
    };
  },
  watch: {
    tab(n) {
      this.$router.replace({ query: { ...this.$route.query, tab: String(n) } }).catch(() => {});
      this.$nextTick(() => this.attachToActiveTab()); // 공유 펫을 새 활성 탭 슬롯으로 이동
    },
  },
  async mounted() {
    this.$nextTick(() => this.attachToActiveTab());
  },
  beforeDestroy() {
    modelHost.detach(); // 대기 홀더로 회수
  },
  methods: {
    // 훈련(TRAIN) 탭은 reaction_keyword_rule 실제 키워드(멘션 제외), 나머지는 mock.
    wordsForLayer(l) {
      return l.key === "TRAIN" ? this.trainWords : l.mockWords;
    },
    // 편집기(BroadcastRuleEditor)에서 키워드룰 변경 시 → 워드클라우드 즉시 재구성(멘션 제외, 빈도 가중)
    onKeywordRulesChanged(rules) {
      const counts = {};
      (rules || [])
        .filter((r) => !r.isMentionMode)
        .forEach((r) => (r.keywords || []).forEach((w) => (counts[w] = (counts[w] || 0) + 1)));
      this.trainWords = Object.entries(counts).map(([word, weight]) => ({ word, weight }));
    },
    // 하나뿐인 공유 펫을 현재 활성 탭 슬롯에 부착(한 번에 한 탭만 보이므로 문제없음).
    //  순서·init-once·크기·카메라·idle은 modelHost.attach가 일괄 책임.
    async attachToActiveTab() {
      const slot = this.$el && this.$el.querySelector(`.stage__pet[data-idx="${this.tab}"]`);
      if (!slot) return;
      await modelHost.attach(slot, { size: 220 });
      // 탭 슬라이드 트랜지션이 끝나 레이아웃이 확정된 뒤 한 번 더 크기/카메라 보정(백지 방지)
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const box = modelHost.api();
          if (box) {
            box.resize(220);
            box.restoreCamera();
          }
          // eager로 숨겨진 채(0 너비) init된 WordCloud(echarts)를 활성 탭 크기로 재측정
          window.dispatchEvent(new Event("resize"));
        }),
      );
    },
    // 룰 편집기의 미리보기 재생: 공유 훈련 모델(modelHost)로 해당 리액션 실행.
    onPlayReaction(reactionId) {
      if (!reactionId) return;
      const box = modelHost.api();
      if (box && box.executeInstruction) box.executeInstruction(reactionId);
    },
  },
};
</script>

<style scoped>
.page-root {
  padding: 24px 28px;
}
/* 시청자 친밀도 탭 (준비 중 플레이스홀더) */
.affinity {
  text-align: center;
  padding: 64px 20px;
}
.affinity__emoji {
  font-size: 40px;
}
.affinity__title {
  font-size: 20px;
  font-weight: 800;
  margin-top: 12px;
}
.affinity__desc {
  color: #8b90a3;
  margin-top: 8px;
}
.layer {
  padding: 4px 4px 24px;
}
.layer__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
.layer__emoji {
  font-size: 34px;
}
.layer__title {
  font-size: 18px;
  font-weight: 800;
}
.layer__meta {
  color: #8e6fd6;
  font-weight: 700;
}
.layer__lifespan {
  font-size: 12px;
  color: #9e93b8;
  margin-top: 2px;
  font-style: italic;
}
.layer__info {
  color: #b6afc7;
  cursor: help;
  vertical-align: middle;
}
.layer__tip-quote {
  margin-top: 6px;
  opacity: 0.8;
}
/* WordCloud(위) + 펫(바로 아래) 를 한 그룹으로 묶어 가운데 정렬 */
.stage {
  margin-top: 12px;
  display: flex;
  justify-content: center;
}
.stage__group {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 520px;
}
.stage__cloud {
  height: 300px;
}
.stage__cloud >>> .brain-chart {
  width: 100%;
  height: 100%;
  max-width: none;
}
.stage__pet {
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-80px); /* WordCloud와 간격 좁힘 */
}
/* 모델 아래 룰 편집기: 가운데 정렬 + 최대 너비 제한 */
.train-rules {
  margin: 0 auto;
  max-width: 640px;
}
</style>
